import { useState, type FormEvent } from 'react';

import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { applyTestUser } from '@/auth/testLogin';

const STORAGE_KEY = 'testLogin.lastIdentity';

interface StoredIdentity {
  tenant: string;
  userId: string;
  site: string;
  appUrl: string;
}

const DEFAULT_IDENTITY: StoredIdentity = {
  tenant: 'local',
  userId: '3',
  site: '3',
  appUrl: 'http://mockapi',
};

const loadStoredIdentity = (): StoredIdentity => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_IDENTITY;
    const parsed = JSON.parse(raw) as Partial<StoredIdentity>;
    return {
      tenant: parsed.tenant ?? DEFAULT_IDENTITY.tenant,
      userId: parsed.userId ?? DEFAULT_IDENTITY.userId,
      site: parsed.site ?? DEFAULT_IDENTITY.site,
      appUrl: parsed.appUrl ?? DEFAULT_IDENTITY.appUrl,
    };
  } catch {
    return DEFAULT_IDENTITY;
  }
};

export const TestLoginRoute = () => {
  const [identity, setIdentity] = useState<StoredIdentity>(loadStoredIdentity);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
      await applyTestUser(identity);
      // Reload so react-oidc-context picks the freshly stored user up from the
      // OIDC store on mount and TestLoginGate flips to the authenticated app.
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      setIsSubmitting(false);
    }
  };

  const updateField = (field: keyof StoredIdentity) => (value: string) =>
    setIdentity((prev) => ({ ...prev, [field]: value }));

  return (
    <Box display="flex" justifyContent="center" pt={8}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ width: 360, maxWidth: '100%' }}
      >
        <Stack gap={3}>
          <Stack gap={1}>
            <Typography variant="h2">Test login</Typography>
            <Typography variant="bodyRegular">
              Dev-only. Generates a self-signed JWT and bypasses the OIDC flow.
            </Typography>
          </Stack>

          <TextField
            label="Tenant"
            value={identity.tenant}
            onChange={(e) => updateField('tenant')(e.target.value)}
            required
            autoFocus
          />
          <TextField
            label="User ID"
            value={identity.userId}
            onChange={(e) => updateField('userId')(e.target.value)}
            required
          />
          <TextField
            label="Site"
            value={identity.site}
            onChange={(e) => updateField('site')(e.target.value)}
            required
          />
          <TextField
            label="App URL"
            value={identity.appUrl}
            onChange={(e) => updateField('appUrl')(e.target.value)}
            required
            helperText="TLP backend the signed user resolves to. Defaults to MSW for e2e."
          />

          {error && <Alert severity="error">{error}</Alert>}

          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            data-automation-id="TestLoginSubmit"
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
